<template>
  <div>

    <!-- #region::Searcher and button -->
    <b-row align-h="between">

      <!-- #region::Searcher -->
      <b-col md="4">
        <BasicSearcher
          :callback="handleSearch"
          placeholder="Buscar marcas"
        />
      </b-col>
      <!-- #endregion::Searcher -->

      <!-- #region::Button for create new models -->
      <b-col
        cols="12"
        md="auto"
      >
        <b-button
          variant="secondary"
          class="my-1 my-md-0 w-100"
          @click="showCreateBrandModal"
        >
          <feather-icon
            icon="PlusIcon"
            class="mr-50"
          />
          <span class="align-middle">Nueva marca</span>
        </b-button>
      </b-col>
      <!-- #endregion::Button for create new models -->

    </b-row>
    <!-- #endregion::Searcher and button -->

    <!-- #region::Skeleton -->
    <template v-if="isLoadingBrands">
      <b-skeleton
        class="mt-1"
        animation="wave"
        width="100%"
        height="430px"
      />
    </template>
    <!-- #endregion::Skeleton -->

    <template v-else>
      <b-card
        class="mt-1 border border-dark shadow-none"
        no-body
      >
        <b-card-text>

          <!-- #region::Table -->
          <b-table
            v-if="availableBrands"
            :items="getBrands"
            :fields="fields"
            responsive
            class="my-0"
          >

            <!-- #region::A virtual column for actions -->
            <template #cell(actions)="data">
              <b-button
                v-b-tooltip.hover.top="'Editar'"
                variant="flat-secondary"
                class="btn-icon rounded-circle"
                @click="showEditBrandModal(data)"
              >
                <feather-icon icon="Edit3Icon" />
              </b-button>
            </template>
            <!-- #region::A virtual column for actions -->

          </b-table>
          <!-- #endregion::Table -->

          <!-- #region::Alert when no brands are available -->
          <b-alert
            v-if="!availableBrands && !isLoadingBrands"
            variant="warning"
            class="my-0"
            show
          >
            <div class="alert-body">
              <span>No se encontraron marcas.</span>
            </div>
          </b-alert>
          <!-- #endregion::Alert when no brands are available -->

        </b-card-text>
      </b-card>
    </template>

    <!-- #region::Pagination & items per list -->
    <BasicPaginator
      v-if="availableBrands"
      :callback="handleChangePagination"
      :total-rows="totalBrands"
    />
    <!-- #endregion::Pagination & items per list -->

    <!-- #region::Modal for creating and editing model -->
    <SaveModal
      ref="saveModal"
      :label-name="'Marca'"
      placeholder="Escribe el nombre de la marca"
      @model-saved="handleModelSaved"
    />
    <!-- #endregion::Modal for creating and editing model -->

  </div>
</template>

<script>
// #region Imports
import { mapActions, mapState, mapGetters } from 'vuex'
import {
  BRow, BCol, BButton, BTable, VBTooltip, BAlert, BSkeleton, BCard, BCardText,
} from 'bootstrap-vue'

import SaveModal from '@/components/modals/SaveModal.vue'
import BasicSearcher from '@/components/forms/BasicSearcher.vue'
import BasicPaginator from '@/components/tables/BasicPaginator.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

import getError from '@/helpers/ErrorsHandler'
// #endregion

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BTable,
    BAlert,
    BButton,
    SaveModal,
    BSkeleton,
    BCardText,
    BasicSearcher,
    BasicPaginator,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      fields: [
        {
          key: 'Name',
          label: 'Nombre',
          thStyle: 'text-transform: none;',
          thClass: 'bg-light',
        },
        {
          key: 'actions',
          label: 'Acciones',
          thStyle: 'text-transform: none;',
          thClass: 'text-right bg-light',
          tdClass: 'text-right',
        },
      ],
    }
  },
  computed: {
    ...mapState('brands', [
      'isLoadingBrands', 'stateResponse', 'totalBrands', 'searchTerm', 'currentPage', 'perPage',
    ]),
    ...mapGetters('brands', ['getBrands']),

    // 14/09/2022 - TAG: True if there are available brands
    availableBrands() {
      return this.totalBrands > 0
    },
  },
  created() {
    // 07/09/2022 - TAG: Load all brands with default params
    this.setSearchTerm('')
    this.loadBrands()
  },
  beforeDestroy() {
    this.setCurrentPage(1)
    this.setPerPage(10)
  },
  methods: {
    ...mapActions('brands', [
      'loadBrands', 'createBrand', 'updateBrand', 'setSearchTerm', 'setCurrentPage', 'setPerPage',
    ]),
    // 07/09/2022 - TAG: Show de modal to create new brand
    showCreateBrandModal() {
      this.$refs.saveModal.showModal('Nueva marca')
    },
    // 07/09/2022 - TAG: Show de modal to edit a existing brand
    showEditBrandModal({ item }) {
      this.$refs.saveModal.showModal('Editar marca', item.IdBrand, item.Name, 'Guardar')
    },
    // 01/09/2022 - TAG: Handle for the paginator component
    handleChangePagination(currentPage, perPage) {
      this.setCurrentPage(currentPage)
      this.setPerPage(perPage)
      this.loadBrands()
    },
    // 01/09/2022 - TAG: Handle for the searcher component
    handleSearch(searchTerm) {
      this.setCurrentPage(1)
      this.setSearchTerm(searchTerm)
      this.loadBrands()
    },
    // 14/09/2022 - TAG: Handle save event from modal to create or update brands
    async handleModelSaved(brandName, brandId = null) {
      try {
        this.$swal({
          title: 'Cargando...',
          allowOutsideClick: false,
        })
        this.$swal.showLoading()

        if (brandId) {
          await this.updateBrand({
            id: brandId,
            name: brandName,
          })
        } else {
          await this.createBrand({
            name: brandName,
          })
        }

        this.$refs.saveModal.hideModal()
        this.showSweetAlert('¡Muy bien!', this.stateResponse.message, 'success')
      } catch (error) {
        this.$swal.close()
        this.showToast('Error de validación', getError(error), 'danger')
      }
    },
    // 13/09/2022 - TAG: Show sweet alert message
    showSweetAlert(title, text, icon) {
      this.$swal({
        title,
        text,
        icon,
        confirmButtonText: 'Aceptar',
        customClass: {
          confirmButton: 'btn btn-primary',
        },
        buttonsStyling: false,
      })
    },
    showToast(title, text, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title, text, variant, icon: 'BellIcon',
        },
      })
    },
  },
}
</script>
